/**
 * Security Verification Script
 * Tests that security configuration works without breaking functionality
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Test that localhost connections are allowed
export const verifyLocalhostAccess = async () => {
  if (!isDevelopment) {
    console.log('🔒 Localhost verification only runs in development mode');
    return { success: true, message: 'Production mode - localhost not needed' };
  }

  console.log('🔍 Verifying localhost access...');

  try {
    // Test a simple fetch to localhost:5000
    const response = await fetch('http://localhost:5000', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log(`✅ Localhost connection successful: ${response.status}`);
    return { success: true, message: 'Localhost access working' };
  } catch (error) {
    console.log(`⚠️ Localhost connection failed: ${error.message}`);
    console.log('💡 This might be expected if your backend is not running on localhost:5000');
    return { success: false, message: error.message };
  }
};

// Test that CSP is not blocking legitimate resources
export const verifyCSPConfiguration = () => {
  console.log('🔒 Verifying CSP configuration...');

  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (!cspMeta) {
    console.error('❌ CSP meta tag not found');
    return { success: false, message: 'CSP meta tag missing' };
  }

  const cspContent = cspMeta.getAttribute('content');
  console.log('✅ CSP meta tag found');

  // Check for localhost in development
  if (isDevelopment) {
    if (cspContent.includes('localhost') || cspContent.includes('127.0.0.1')) {
      console.log('✅ Localhost allowed in CSP for development');
    } else {
      console.warn('⚠️ Localhost not found in CSP - this might cause issues');
    }
  }

  return { success: true, message: 'CSP configuration verified' };
};

// Test that external resources load properly
export const verifyExternalResources = () => {
  console.log('🌐 Verifying external resources...');

  const externalLinks = document.querySelectorAll('link[href*="https://"]');
  let sriCount = 0;

  externalLinks.forEach((link) => {
    if (link.integrity) {
      sriCount++;
      console.log(`✅ External resource with SRI: ${link.href}`);
    } else {
      console.log(`⚠️ External resource without SRI: ${link.href}`);
    }
  });

  console.log(
    `📊 Found ${sriCount} resources with SRI out of ${externalLinks.length} external resources`
  );

  return {
    success: true,
    message: `External resources verified (${sriCount}/${externalLinks.length} with SRI)`,
  };
};

// Run all verification tests
export const runSecurityVerification = async () => {
  if (!isDevelopment) {
    console.log('🔒 Security verification only runs in development mode');
    return;
  }

  console.log('🚀 Running security verification...');
  console.log('='.repeat(50));

  const results = [];

  // Test CSP
  results.push(verifyCSPConfiguration());

  // Test external resources
  results.push(verifyExternalResources());

  // Test localhost access
  results.push(await verifyLocalhostAccess());

  console.log('='.repeat(50));

  const passed = results.filter((r) => r.success).length;
  const total = results.length;

  console.log(`📋 Verification Results: ${passed}/${total} passed`);

  if (passed === total) {
    console.log('🎉 All security verifications passed!');
  } else {
    console.log('⚠️ Some verifications failed - check the details above');
  }

  return results;
};

// Auto-run verification in development
if (isDevelopment && typeof window !== 'undefined') {
  // Run verification after a short delay to ensure everything is loaded
  setTimeout(() => {
    runSecurityVerification();
  }, 1500);
}
